import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import Modal from "@/components/Modal";
import HybridResearchPanel from "@/components/HybridResearchPanel";
import {
  FileText, Loader2, X, AlertTriangle, Download, Sparkles, Calendar,
  Target, ListChecks, Bell, UploadCloud, ClipboardList,
} from "lucide-react";

function fmtErr(d) {
  if (!d) return "Erreur.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(d);
}

const SEV_COLORS = { critical: "#E24B4A", warning: "#EF9F27", info: "#378ADD" };
const PRIO_COLORS = { haute: "#E24B4A", high: "#E24B4A", moyenne: "#EF9F27", medium: "#EF9F27", basse: "#1D9E75", low: "#1D9E75" };

export default function BriefComiteModal({ onClose }) {
  const [step, setStep] = useState("upload"); // upload | analyzing | preview | exporting
  const [file, setFile] = useState(null);
  const [hint, setHint] = useState("");
  const [error, setError] = useState("");
  const [brief, setBrief] = useState(null);
  const inputRef = useRef(null);

  const generate = async (e) => {
    e?.preventDefault();
    if (!file) { setError("Sélectionnez un PDF."); return; }
    setStep("analyzing"); setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (hint) fd.append("topic_hints", hint);
      const r = await api.post("/workspace/brief-comite/generate", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      setBrief(r.data.brief);
      setStep("preview");
    } catch (err) {
      setError(fmtErr(err?.response?.data?.detail || err.message));
      setStep("upload");
    }
  };

  const exportPdf = async () => {
    if (!brief) return;
    setStep("exporting");
    try {
      const r = await api.post("/workspace/brief-comite/export-pdf", brief, {
        responseType: "blob",
        timeout: 60000,
      });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `brief-comite-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setStep("preview");
    } catch (err) {
      setError(fmtErr(err?.response?.data?.detail || err.message));
      setStep("preview");
    }
  };

  return (
    <Modal onClose={onClose} maxWidth="max-w-4xl" testid="brief-comite-modal">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="noam-overline flex items-center gap-1.5 text-[#EF9F27]"><ClipboardList className="w-3 h-3" /> Brief de comité</div>
          <div className="noam-display text-lg font-semibold mt-1">Synthèse exécutive en 1 page</div>
          <p className="text-[11px] text-zinc-500 mt-1.5">Uploade un ordre du jour PDF — NOAM croise avec tes risques, leads, opportunités et alertes pour produire un briefing prêt à présenter en COMEX/CODIR.</p>
        </div>
        <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5"><X className="w-4 h-4" /></button>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 mb-4 rounded-md border border-[#E24B4A]/30 bg-[#E24B4A]/10 text-[#FCA5A5] text-xs">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5" /><span>{error}</span>
        </div>
      )}

      {step === "upload" && (
        <form onSubmit={generate} className="space-y-4">
          <div onClick={() => inputRef.current?.click()}
            className="border-2 border-dashed border-white/10 rounded-lg p-10 text-center cursor-pointer hover:border-[#EF9F27]/60 hover:bg-[#EF9F27]/5 transition-colors"
            data-testid="brief-dropzone">
            <UploadCloud className="w-8 h-8 mx-auto text-zinc-400" />
            <div className="text-sm text-white mt-3">{file ? file.name : "Cliquer pour sélectionner l'ordre du jour"}</div>
            <div className="text-[11px] text-zinc-500 mt-1">PDF uniquement — max 12 Mo</div>
          </div>
          <input ref={inputRef} type="file" accept=".pdf" hidden
            onChange={(e) => { setFile(e.target.files?.[0]); setError(""); }} data-testid="brief-file-input" />

          <div>
            <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Indications (facultatif)</label>
            <input value={hint} onChange={(e) => setHint(e.target.value)}
              placeholder="Ex : insister sur le sujet cyber, point spécifique sur la liquidité…"
              className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#EF9F27] focus:outline-none" data-testid="brief-hint" />
          </div>

          <button type="submit" disabled={!file} className="btn-primary w-full justify-center disabled:opacity-50" data-testid="brief-generate-btn">
            <Sparkles className="w-3.5 h-3.5" /> Générer le briefing
          </button>
        </form>
      )}

      {step === "analyzing" && (
        <div className="py-12 text-center" data-testid="brief-analyzing">
          <Loader2 className="w-8 h-8 text-[#EF9F27] animate-spin mx-auto" />
          <div className="text-white mt-4 text-sm">NOAM croise votre ordre du jour avec les risques, leads, opportunités et alertes…</div>
          <div className="text-[11px] text-zinc-500 mt-1">Génération du briefing structuré dans votre langue préférée</div>
        </div>
      )}

      {step === "preview" && brief && <BriefPreview brief={brief} onExport={exportPdf} onClose={onClose} />}

      {step === "preview" && brief && (
        <div className="mt-5 pt-4 border-t border-white/10">
          <HybridResearchPanel
            contextModule="brief"
            defaultMode="hybrid"
            title="Enrichir le brief avec signaux web temps réel"
            testid="brief-hybrid-research"
            compact
            initialTopic={brief?.title || ""}
          />
        </div>
      )}

      {step === "exporting" && (
        <div className="py-12 text-center"><Loader2 className="w-8 h-8 text-[#EF9F27] animate-spin mx-auto" /><div className="text-white mt-3 text-sm">Génération du PDF…</div></div>
      )}
    </Modal>
  );
}

function BriefPreview({ brief, onExport, onClose }) {
  return (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1" data-testid="brief-preview">
      <div className="rounded-md border border-[#EF9F27]/40 bg-[#EF9F27]/5 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-white text-base font-semibold">{brief.title}</div>
            <div className="text-[11px] text-zinc-500 mt-1 flex items-center gap-2">
              <Calendar className="w-3 h-3" /> {brief.date_iso || new Date().toISOString().slice(0, 10)}
              {brief.organisation_name && <><span>·</span><span>{brief.organisation_name}</span></>}
              <span className="ml-1 px-1.5 py-0.5 rounded bg-white/5 text-[10px] uppercase">{brief.locale || "fr"}</span>
            </div>
            {brief.summary && <p className="text-zinc-300 text-xs mt-2">{brief.summary}</p>}
          </div>
        </div>
      </div>

      {brief.top_topics?.length > 0 && (
        <Section icon={Target} color="#7F77DD" title="Top sujets prioritaires">
          {brief.top_topics.map((t, i) => {
            const c = PRIO_COLORS[(t.priority || "").toLowerCase()] || "#A1A1AA";
            return (
              <div key={`tt-${t.title || ""}-${i}`} className="border border-white/5 rounded-md p-3 space-y-1.5" data-testid={`brief-topic-${i}`}>
                <div className="flex items-center gap-2 text-xs">
                  <span className="px-1.5 py-0.5 rounded text-[10px] uppercase font-semibold" style={{ background: `${c}1A`, color: c, border: `1px solid ${c}55` }}>{t.priority || "—"}</span>
                  <span className="text-white font-medium">{t.title}</span>
                </div>
                <div className="text-[11px] text-zinc-400">{t.why}</div>
                {t.recommendation && <div className="text-[11px] text-[#1D9E75]">→ {t.recommendation}</div>}
                {t.linked?.length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-1">
                    {t.linked.map((l) => <span key={l} className="text-[10px] px-1.5 py-0.5 rounded bg-[#378ADD]/10 text-[#B5D4F4] border border-[#378ADD]/30">{l}</span>)}
                  </div>
                )}
              </div>
            );
          })}
        </Section>
      )}

      {brief.strategic_readings?.length > 0 && (
        <Section icon={Sparkles} color="#378ADD" title="Lectures stratégiques">
          {brief.strategic_readings.map((r, i) => (
            <div key={`sr-${r.phase || ""}-${i}`} className="border border-white/5 rounded-md p-3 space-y-1.5" data-testid={`brief-reading-${i}`}>
              <div className="text-[10px] uppercase tracking-wider text-[#378ADD] font-semibold">{r.phase}</div>
              <div className="text-white text-xs">{r.reading}</div>
              {r.talking_points?.length > 0 && (
                <ul className="space-y-0.5 pl-3">
                  {r.talking_points.map((tp, j) => <li key={`tp-${j}`} className="text-[11px] text-zinc-400 list-disc">{tp}</li>)}
                </ul>
              )}
            </div>
          ))}
        </Section>
      )}

      {brief.open_actions?.length > 0 && (
        <Section icon={ListChecks} color="#1D9E75" title="Actions ouvertes pertinentes">
          <table className="w-full text-xs">
            <thead className="text-zinc-500">
              <tr className="border-b border-white/5"><th className="text-left p-2">Action</th><th className="text-left p-2">Owner</th><th className="text-left p-2">Échéance</th><th className="text-left p-2">Statut</th></tr>
            </thead>
            <tbody>
              {brief.open_actions.map((a, i) => (
                <tr key={`oa-${a.title || ""}-${i}`} className="border-b border-white/5" data-testid={`brief-action-${i}`}>
                  <td className="p-2 text-white">{a.title}</td>
                  <td className="p-2 text-zinc-400">{a.owner || "—"}</td>
                  <td className="p-2 text-zinc-400">{a.echeance || "—"}</td>
                  <td className="p-2 text-zinc-400">{a.statut || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {brief.alerts?.length > 0 && (
        <Section icon={Bell} color="#E24B4A" title="Alertes du jour">
          {brief.alerts.map((al, i) => {
            const c = SEV_COLORS[(al.severity || "info").toLowerCase()] || "#378ADD";
            return (
              <div key={`al-${al.title || ""}-${i}`} className="flex items-start gap-3 py-1.5" data-testid={`brief-alert-${i}`}>
                <span className="w-2 h-2 rounded-full shrink-0 mt-1.5" style={{ background: c }} />
                <div className="flex-1">
                  <div className="text-white text-xs font-medium">{al.title}</div>
                  <div className="text-[11px] text-zinc-400">{al.detail}</div>
                </div>
                <span className="text-[10px] uppercase tracking-wider" style={{ color: c }}>{al.severity}</span>
              </div>
            );
          })}
        </Section>
      )}

      <div className="flex justify-end gap-2 pt-3 border-t border-white/5">
        <button onClick={onClose} className="text-xs px-3 py-1.5 rounded-md border border-white/10 hover:bg-white/5">Fermer</button>
        <button onClick={onExport} className="btn-primary text-xs" data-testid="brief-export-btn">
          <Download className="w-3.5 h-3.5" /> Exporter en PDF
        </button>
      </div>
    </div>
  );
}

function Section({ icon: Icon, color, title, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2"><Icon className="w-3.5 h-3.5" style={{ color }} /><span className="noam-overline" style={{ color }}>{title}</span></div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

export function BriefComiteButton({ canUse, allowedModules, fullWidth = false }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  if (!canUse) return null;
  if (allowedModules && !allowedModules.includes("brief_comite")) return null;
  return (
    <>
      <button onClick={() => setOpen(true)}
        className={
          fullWidth
            ? "w-full inline-flex items-center gap-2 text-xs text-zinc-200 hover:text-white transition-colors text-left"
            : "inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-[#EF9F27]/40 text-[#FCD9A2] hover:bg-[#EF9F27]/10 transition-colors"
        }
        data-testid="topbar-brief-btn">
        <ClipboardList className="w-3.5 h-3.5 text-[#EF9F27]" /> {t("saas.brief.button", { defaultValue: "Brief de comité" })}
      </button>
      {open && <BriefComiteModal onClose={() => setOpen(false)} />}
    </>
  );
}
